'use client';

import { useEffect, useRef, useSyncExternalStore } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import Image from 'next/image';
import { Heart, Bed, Bath, Square, Play, Box } from 'lucide-react';
import { classifyTour } from '@/lib/virtualTour';
import type { Listing } from '@/types/listing';
import {
  formatPrice,
  formatPriceFull,
  formatBaths,
  generateListingSlug,
  titleCaseCity,
  isNewListing,
  localTodayISO,
  pluralize,
  cn,
} from '@/lib/utils';
import {
  factLine,
  formatBedsFact,
  formatBathsFact,
  formatSqftFact,
} from '@/lib/listingFacts';
import {
  availabilityOf,
  badgeClasses,
  badgeLabel,
  isGone,
  MISSING,
  type Availability,
} from '@/lib/availability';
import { useSearchStore } from '@/stores/searchStore';
import { useAuthStore } from '@/stores/authStore';
import { useFavoritesStore } from '@/stores/favoritesStore';
import { startGuestFavorite } from '@/lib/favoriteIntent';
import { useNearViewport } from '@/hooks/useInView';
import { useListingPhotos } from '@/hooks/useListingPhotos';
import { photoSrc } from '@/lib/photos';
import photoLoader from '@/lib/imageLoader';
import { listingViewTransitionName } from '@/lib/listingViewTransition';
import { listedAgoLabel } from '@/lib/listedAgo';
import PhotoCarousel from './PhotoCarousel';
import {
  CARD_GEOMETRY,
  CARD_MOTION,
  SAVE_LABEL,
  SHEEN_STYLE,
} from './ListingCard.tokens';

// Keep the existing module export available to internal consumers while the
// implementation lives in the shared identity helper.
export { listingViewTransitionName } from '@/lib/listingViewTransition';

// Stable no-op subscription: the "store" (today's date) never pushes updates.
const subscribeNever = () => () => {};

interface ListingCardProps {
  listing: Listing;
  variant?: 'default' | 'compact';
  /** Mark the first cards above the fold to prioritize their images (LCP). */
  priority?: boolean;
  /** Suppress the overlay heart (e.g. the favorites page, where every card is
   *  already saved and removal is an explicit, labeled control). */
  hideFavorite?: boolean;
  /**
   * The catalog no longer returns this home at all (a 404, or an id the batch
   * response omitted). Stronger than any status the caller still holds — the row
   * is gone, so whatever the stale snapshot says about it is not worth repeating.
   */
  missing?: boolean;
  /**
   * Painted width of the card photo, for `srcset` selection. The default
   * describes the standard `sm:grid-cols-2 lg:grid-cols-3` grid inside a
   * `max-w-7xl` page (city, favorites, similar homes, price cuts, open houses),
   * where the ≥1024 column is a fixed **389 px** — not a viewport fraction —
   * because the container is capped at 1280. The split-view search panel is much
   * narrower, so it passes its own (see `search-client`).
   */
  photoSizes?: string;
  /**
   * Disable named boundaries when this card is a repeated copy in another
   * rail. The card remains fully interactive; only the global transition name
   * is omitted so native view transitions never see duplicate identities.
   */
  viewTransitionEnabled?: boolean;
}

/** Card photo box for the standard 1 / 2 / 3-column grid in a max-w-7xl page. */
export const DEFAULT_CARD_PHOTO_SIZES =
  '(max-width: 639px) 92vw, (max-width: 1023px) 46vw, 389px';

type ViewTransitionDocument = Document & {
  startViewTransition?: (
    update: () => void | Promise<void>
  ) => { finished: Promise<unknown> };
};

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';
const subscribeReducedMotion = (onChange: () => void) => {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return () => {};
  const media = window.matchMedia(REDUCED_MOTION_QUERY);
  media.addEventListener?.('change', onChange);
  return () => media.removeEventListener?.('change', onChange);
};
const reducedMotionSnapshot = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia(REDUCED_MOTION_QUERY).matches;
const serverReducedMotionSnapshot = () => false;

export default function ListingCard({
  listing,
  variant = 'default',
  priority = false,
  hideFavorite = false,
  missing = false,
  photoSizes = DEFAULT_CARD_PHOTO_SIZES,
  viewTransitionEnabled = true,
}: ListingCardProps) {
  // "New" is a calendar question in the VIEWER's timezone; computing it during
  // SSR (ISR-cached, server clock) diverges from hydration and throws React
  // #418. Server snapshot is null so the badge only renders client-side.
  // (Object.is on equal date strings keeps the client snapshot stable.)
  const today = useSyncExternalStore(subscribeNever, localTodayISO, () => null);
  const { hoveredListingId, selectedListingId, setHoveredListing } = useSearchStore();
  const router = useRouter();
  const { user } = useAuthStore();
  const { isFavorite, toggleFavorite } = useFavoritesStore();
  const isHovered = hoveredListingId === listing.id;
  // A map pin click / open preview selects the home; the matching list card
  // highlights and scrolls into view so the map and list stay in lock-step.
  const isSelected = selectedListingId === listing.id;
  const isActive = isHovered || isSelected;
  const cardRef = useRef<HTMLAnchorElement>(null);
  // A listing can reach this card photoless while claiming 51 photos — the map's
  // light projection omits them, the search index lags the database on brand-new
  // homes, and a client refetch undoes the server's backfill. The hook keeps the
  // photos this session has already seen and, failing that, goes and gets them.
  const inView = useNearViewport(cardRef, priority);
  const { photos, pending: photosPending } = useListingPhotos(listing, inView);
  const reducedMotion = useSyncExternalStore(
    subscribeReducedMotion,
    reducedMotionSnapshot,
    serverReducedMotionSnapshot
  );
  useEffect(() => {
    if (isSelected && variant === 'default') {
      cardRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [isSelected, variant]);
  const isFavorited = isFavorite(listing.id);
  const priceCut =
    (listing.originalListPrice ?? 0) > listing.price
      ? listing.originalListPrice! - listing.price
      : 0;
  /**
   * The cut as a share of the original ask, alongside the dollars.
   *
   * $38,000 off means something completely different on a $750k home than on a
   * $4M one, and the dollar figure alone is what let the feed lead with "up to
   * $4.4M off" — true, enormous, and useless to a buyer with a $1.2M budget. The
   * percentage also makes a broken record legible on sight: a badge reading
   * "(90%)" is visibly a dropped digit in the original price, not the deal of the
   * century. (W14 R138)
   */
  const priceCutPct = priceCut > 0 ? Math.round((priceCut * 100) / listing.originalListPrice!) : 0;

  /**
   * Whether this home can still be bought. Search only ever hands this card
   * active listings, but favourites, the recently-viewed rail, "picked for you"
   * and a compare thumbnail all arrive **by id**, where the status is whatever
   * the catalog holds — and roughly a third of the corpus is being expired as the
   * feed roll-call clears stale rows. A `missing` id outranks the status: the row
   * is gone, so the snapshot's status is no longer worth repeating. (W16 R151)
   */
  const availability: Availability = missing ? MISSING : availabilityOf(listing.status);
  const statusBadge = badgeLabel(availability);
  // 44% of listings carry one; null for the rest, and for anything the feed
  // stored that is not a usable http(s) URL.
  const tour = classifyTour(listing.virtualTourUrl);
  const gone = isGone(availability);

  const listingUrl = generateListingSlug(listing);
  const transitionId = viewTransitionEnabled ? listing.mlsListingId : undefined;
  const photoTransitionName = reducedMotion
    ? undefined
    : listingViewTransitionName(transitionId, 'photo');
  const metaTransitionName = reducedMotion
    ? undefined
    : listingViewTransitionName(transitionId, 'meta');

  /**
   * Opt into the native navigation transition only when the platform exposes
   * it. `onNavigate` is SPA-only, so modified clicks, keyboard activation,
   * external links, and browsers without the API keep Next's normal Link path.
   */
  const handleNavigate = (event: { preventDefault: () => void }) => {
    if (reducedMotion || typeof document === 'undefined') return;
    const startViewTransition = (document as ViewTransitionDocument).startViewTransition;
    if (typeof startViewTransition !== 'function') return;

    event.preventDefault();
    const navigate = () =>
      router.push(listingUrl, {
        transitionTypes: ['listing-detail'],
      });
    try {
      const transition = startViewTransition.call(document, navigate);
      transition.finished.catch(() => {
        // A navigation failure still leaves the normal router state as the
        // source of truth; avoid an unhandled rejection from the transition.
      });
    } catch {
      // A partially implemented browser API should never strand the link.
      navigate();
    }
  };

  /**
   * The sheen follows the pointer across the photograph.
   *
   * Written straight onto the frame's own custom properties rather than through
   * React state: this fires at the pointer's sample rate, and a `setState` per
   * sample would re-render every card in a 40-card grid's hovered subtree. The
   * two properties are read by `SHEEN_STYLE`, which carries its own 50%/50%
   * fallbacks, so the highlight is correct before this has ever run.
   *
   * No rotation, no perspective, no tilt: the highlight moves, the card does not
   * turn.
   */
  const trackSheen = (e: React.PointerEvent<HTMLDivElement>) => {
    const frame = e.currentTarget;
    const box = frame.getBoundingClientRect();
    if (box.width === 0 || box.height === 0) return;
    frame.style.setProperty('--sheen-x', `${((e.clientX - box.left) / box.width) * 100}%`);
    frame.style.setProperty('--sheen-y', `${((e.clientY - box.top) / box.height) * 100}%`);
  };

  const handleFavorite = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (!user) {
      // Remember the home + full filtered URL; sign-in returns here and saves it.
      startGuestFavorite(listing.id);
      return;
    }

    try {
      await toggleFavorite(listing.id);
    } catch (err) {
      console.error('Failed to toggle favorite:', err);
    }
  };

  if (variant === 'compact') {
    return (
      <Link
        href={listingUrl}
        transitionTypes={reducedMotion ? [] : ['listing-detail']}
        onNavigate={handleNavigate}
        className={cn(
          'flex gap-3 rounded-xl p-3 transition-all',
          isHovered ? 'bg-emerald-500/10 ring-1 ring-emerald-500/40' : 'hover:bg-glass'
        )}
        onMouseEnter={() => setHoveredListing(listing.id)}
        onMouseLeave={() => setHoveredListing(null)}
      >
        {/* Thumbnail */}
        <div
          className="relative h-20 w-24 flex-shrink-0 overflow-hidden rounded-lg"
          style={photoTransitionName ? { viewTransitionName: photoTransitionName } : undefined}
          data-view-transition-name={photoTransitionName}
          data-transition-type={photoTransitionName ? 'listing-detail' : undefined}
        >
          <Image
            src={photoSrc(photos[0], 'micro')}
            loader={photoLoader}
            alt={listing.address}
            fill
            className="object-cover"
            sizes="96px"
          />
        </div>

        {/* Info */}
        <div
          className="min-w-0 flex-1"
          style={metaTransitionName ? { viewTransitionName: metaTransitionName } : undefined}
          data-view-transition-name={metaTransitionName}
        >
          <p className="font-bold tabular-nums text-ink">{formatPriceFull(listing.price)}</p>
          <p className="truncate text-sm text-ink-soft">{listing.address}</p>
          {/* `factLine` omits what the feed did not carry. This printed
              "0 bd · 0 ba · 0 sqft" on every land and mobile-home listing. */}
          {factLine(listing) && <p className="text-sm text-ink-muted">{factLine(listing)}</p>}
        </div>
      </Link>
    );
  }

  return (
    <Link
      ref={cardRef}
      href={listingUrl}
      transitionTypes={reducedMotion ? [] : ['listing-detail']}
      onNavigate={handleNavigate}
      className={cn(
        'group',
        CARD_GEOMETRY.frame,
        CARD_MOTION.frame,
        isActive
          ? cn(CARD_MOTION.liftActive, 'border-emerald-500/50 shadow-glow-emerald')
          : cn(
              CARD_MOTION.liftHover,
              'border-hairline hover:border-hairline-strong hover:shadow-lift-lg'
            )
      )}
      onMouseEnter={() => setHoveredListing(listing.id)}
      onMouseLeave={() => setHoveredListing(null)}
    >
      {/* Photo carousel */}
      <div className="relative overflow-hidden" onPointerMove={trackSheen}>
        <div className={CARD_MOTION.photo}>
          <PhotoCarousel
            photos={photos}
            listingId={listing.id}
            mlsListingId={listing.mlsListingId}
            viewTransitionEnabled={viewTransitionEnabled}
            alt={listing.address}
            sizes={photoSizes}
            aspectRatio={CARD_GEOMETRY.photo}
            priority={priority}
            pending={photosPending}
          />
        </div>

        {/* The sheen. Above the photograph, below the badges, and below the
            "gone" scrim — a home that can no longer be bought should not catch
            the light. `pointer-events-none` so it never takes a click away from
            the carousel arrows or the heart underneath it. */}
        <span
          aria-hidden
          data-sheen
          style={SHEEN_STYLE}
          className={cn('pointer-events-none absolute inset-0 z-[4]', CARD_MOTION.sheen)}
        />

        {/* Availability badge.
            Solid, not tinted: this sits **over the photograph**, where the
            paper-canvas tint it used to carry (`bg-coral-500/15 text-coral-300`)
            was effectively transparent and left dark-red type on whatever the
            photo happened to be. And the label is what a buyer needs to know
            ("No longer listed") rather than the MLS's word for it ("Expired"),
            which says nothing about whether the home can still be bought. */}
        {statusBadge && (
          <span
            className={cn(
              'absolute left-3 top-3 z-10 rounded-full px-2.5 py-1 text-xs font-semibold shadow-sm',
              badgeClasses(availability)
            )}
          >
            {statusBadge}
          </span>
        )}

        {/* A gone home is dimmed as well as badged: at a glance down a grid of
            saved homes the photo is what carries "still there or not", and a
            single pill in the corner is easy to miss. */}
        {gone && <span aria-hidden className="absolute inset-0 z-[5] bg-canvas/45" />}

        {/* Fresh-on-market badge (active listings only; status badge owns that slot otherwise) */}
        {!statusBadge && today !== null && isNewListing(listing.listDate, today) && (
          <span className="absolute left-3 top-3 z-10 rounded-full bg-emerald-500 px-2.5 py-1 text-xs font-semibold text-canvas">
            New
          </span>
        )}

        {/* Virtual-tour badge.
            44% of active listings carry a tour URL and, until this, the only
            place any of them surfaced was a text link at the foot of the detail
            page — so nearly half the homes in any grid had a walkthrough behind
            them and nothing on screen said so.
            BOTTOM-LEFT because the other three corners are taken: status and
            "New" own top-left, the save control owns top-right, and the price
            sits below the photo. It is a LABEL, not a link — the whole card is
            already one link, and a nested anchor is invalid HTML and a
            keyboard-navigation trap. Tapping the card and then the tour is the
            correct path; this only has to say the tour exists. */}
        {tour && (
          <span
            /* bg-ink/65, not /55: measured 3.78:1 over white where 12px text
               needs 4.5. A scrim is either sufficient for any photograph the MLS
               can send or it is not, and white is the brightest one it can send —
               over the actual photos behind it this same badge read anywhere from
               4.38:1 to 12.42:1, which is exactly the data-dependent verdict
               contrast.spec.ts exists to refuse. /65 is the value the photo
               counter on this card and the gallery badge already use and pass at. */
            className="glass-blur absolute bottom-3 left-3 z-10 flex items-center gap-1.5 rounded-full bg-ink/65 px-2.5 py-1 text-xs font-semibold text-white"
            /* The card's accessible name is the address and price. Appending
               this keeps the tour discoverable to a screen reader without
               inventing a second focus stop on a card that is one control. */
            aria-hidden={false}
          >
            {tour.kind === 'threeD' ? (
              <Box className="h-3.5 w-3.5" aria-hidden />
            ) : (
              <Play className="h-3.5 w-3.5" aria-hidden fill="currentColor" />
            )}
            {tour.label}
          </span>
        )}

        {/* Save control (suppressed where removal is an explicit control).
            A toggle, so it carries `aria-pressed` — and its name changes with
            it, because the name is what a voice-control user says and what a
            switch user reads in a list of controls. */}
        {!hideFavorite && (
          <button
            type="button"
            onClick={handleFavorite}
            className={cn(
              'glass-blur absolute right-3 top-3 z-10 flex h-9 w-9 items-center justify-center rounded-full',
              CARD_MOTION.heart,
              isFavorited
                ? 'bg-coral-500 hover:bg-coral-400'
                : 'bg-ink/55 hover:bg-ink/70'
            )}
            aria-pressed={isFavorited}
            aria-label={isFavorited ? SAVE_LABEL.saved : SAVE_LABEL.idle}
          >
            <Heart
              className={cn(
                'h-4.5 w-4.5 transition-colors',
                isFavorited ? 'fill-current text-white' : 'text-white'
              )}
              fill={isFavorited ? 'currentColor' : 'none'}
            />
          </button>
        )}
      </div>

      {/* Content */}
      <div
        className={CARD_GEOMETRY.body}
        style={metaTransitionName ? { viewTransitionName: metaTransitionName } : undefined}
        data-view-transition-name={metaTransitionName}
      >
        {/* Price */}
        {/* `flex-wrap`: the cut badge carries two numbers now, and at a 78vw card
            "$2,499,999 · ▼ $1.5M (38%) · $965/sqft" does not fit one line. Wrapping
            the row drops `$/sqft` to a second line instead of breaking the pill
            across two, which is what it did before. (W14 R138) */}
        <div className={CARD_GEOMETRY.priceRow}>
          <p className={CARD_GEOMETRY.price}>
            {formatPriceFull(listing.price)}
            {priceCut > 0 && (
              <span
                className="whitespace-nowrap rounded-full border border-gold-500/25 bg-gold-500/10 px-2 py-0.5 text-xs font-semibold tabular-nums text-gold-300"
                aria-label={`${formatPriceFull(priceCut)} below the original ask, a ${priceCutPct}% cut`}
              >
                ▼ {formatPrice(priceCut)} ({priceCutPct}%)
              </span>
            )}
          </p>
          {(listing.pricePerSqft ?? 0) > 0 && (
            <p className={CARD_GEOMETRY.ppsf}>
              ${Math.round(listing.pricePerSqft!)}/sqft
            </p>
          )}
        </div>

        {/* Details — each fact only when the feed carried one.
            The compact variant above uses `factLine`; this iconed variant needs
            its own icons, so it asks the same `known…` questions per fact rather
            than re-splitting a joined string. Both must agree: this one still
            printed "0 bd" on the city pages after the compact one was fixed. */}
        <div className={CARD_GEOMETRY.facts}>
          {formatBedsFact(listing.beds) && (
            <span className="flex items-center gap-1.5">
              <Bed className="h-4 w-4" />
              <span className="font-medium text-ink-soft">{listing.beds}</span>
              <span className="text-sm">bd</span>
            </span>
          )}
          {formatBathsFact(listing.baths) && (
            <span className="flex items-center gap-1.5">
              <Bath className="h-4 w-4" />
              <span className="font-medium text-ink-soft">{formatBaths(listing.baths)}</span>
              <span className="text-sm">ba</span>
            </span>
          )}
          {formatSqftFact(listing.sqft) && (
            <span className="flex items-center gap-1.5">
              <Square className="h-4 w-4" />
              <span className="font-medium tabular-nums text-ink-soft">
                {listing.sqft.toLocaleString()}
              </span>
              <span className="text-sm">sqft</span>
            </span>
          )}
        </div>

        {/* Address */}
        <p className={CARD_GEOMETRY.address}>{listing.address}</p>
        <p className={CARD_GEOMETRY.locality}>
          {titleCaseCity(listing.city)}, {listing.state} {listing.zip}
        </p>

        {/* Time since listing + IDX attribution.
            "Listed N days ago" rather than "N days on market": the feed's
            DaysOnMarket is empty for every listing, so this number is always
            calendar days since `list_date` and is never reset by a relist.
            Days-since-listed is literally what we can observe; "on market"
            would claim a measurement of how long the home has been for sale. */}
        {listedAgoLabel(listing.daysOnMarket, listing.listDate) && (
          <p className={CARD_GEOMETRY.age}>
            {listedAgoLabel(listing.daysOnMarket, listing.listDate)}
          </p>
        )}
        {listing.listingOfficeName && (
          <p className="mt-1 truncate text-xs text-ink-faint">
            Listing courtesy of {listing.listingOfficeName}
          </p>
        )}
      </div>
    </Link>
  );
}
